import React from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

/**
 * Catches render/runtime errors from its subtree and shows a recoverable fallback instead of
 * crashing the whole app. Used to isolate the canvas and properties panel so one misbehaving
 * flow component cannot take down the entire builder.
 *
 * Props: `label` (heading text), optional `fallback(error, reset)` render prop, `onReset`.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, info?.componentStack);
  }

  reset = () => {
    this.setState({ error: null });
    if (this.props.onReset) this.props.onReset();
  };

  render() {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }
    if (this.props.fallback) {
      return this.props.fallback(error, this.reset);
    }
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={this.reset}>
              Retry
            </Button>
          }
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {this.props.label || 'Something went wrong'}
          </Typography>
          <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
            {error.message || String(error)}
          </Typography>
        </Alert>
      </Box>
    );
  }
}

export default ErrorBoundary;
